﻿/**
 * dgFav - Distribution Gauthier Favorites API v4.0
 * ═══════════════════════════════════════════════
 * Single source of truth for favorites + custom folders (multi-tag).
 * Works for guests (localStorage only) and logged-in users (+ Shopify App Proxy sync).
 *
 * NEW v4: Customer-defined folders. A favorite handle can belong to 0..N folders.
 *   Folders are pure tags ; the master `handles` list remains authoritative
 *   for "is this favorited?" — toggling the heart never affects folder content
 *   (except `remove()` which retires a handle from every folder for consistency).
 *
 * v6: favorites are VARIANT-level. The identity string is an item key
 *   ('v:<variantId>'), not a product handle — a product handle could not tell the
 *   Caisse from the Unité, which is the whole point of a B2B re-order list, and it
 *   changed whenever a product was renamed (killing the favorite silently).
 *   The `handles` field name is kept because every consumer treats the value as an
 *   opaque string; only `keyInfo()` looks inside one. `meta` carries what is needed
 *   to draw the card; `revision` is the optimistic-concurrency guard that keeps a
 *   customer and the Command Center from overwriting each other.
 *
 * State shape (in-memory, localStorage, and Worker payload):
 *   {
 *     handles: string[],                       // item keys
 *     meta: { [key]: {h, s, t, u, av} },       // handle, sku, title, isUnit, available
 *     folders: [{ id, name, color, handles: string[] }],
 *     revision: number
 *   }
 * On the wire the same state is sent as { revision, items, meta, folders[].items }.
 * Legacy inputs ({handles} / bare array) are still accepted on read.
 *
 * Closed color palette:
 *   red, orange, yellow, green, teal, blue, purple, pink
 *
 * API:
 *   dgFav.list()                          -> string[]   master handle list
 *   dgFav.has(handle)                     -> boolean
 *   dgFav.toggle(handle)                  -> boolean    new wishlisted state
 *   dgFav.remove(handle)                  -> void       removes from master AND every folder
 *   dgFav.onChange(fn)                    -> void       fn(handles, folders)
 *   dgFav.syncFromServer()                -> Promise
 *   dgFav.syncToServer()                  -> Promise
 *
 *   dgFav.folders()                       -> Folder[]
 *   dgFav.foldersOf(handle)               -> string[]   folder ids containing this handle
 *   dgFav.createFolder(name, color?)      -> Folder
 *   dgFav.renameFolder(id, name)          -> boolean
 *   dgFav.setFolderColor(id, color)       -> boolean
 *   dgFav.deleteFolder(id)                -> boolean    folder gone, handles preserved
 *   dgFav.addToFolder(folderId, handle)   -> boolean
 *   dgFav.removeFromFolder(folderId, h)   -> boolean
 *   dgFav.colors()                        -> string[]   available palette
 *
 *   dgFav.isSyncEnabled()                 -> boolean
 *
 * Sync model (v5.2):
 *   • Pull: server replaces local unless local has unsynced edits / pending deletions.
 *   • Push: full state replace on server; GET verify after POST (retry if mismatch).
 *   • Bootstrap: server empty + local has data → push once.
 *
 * Config (injected by theme.liquid):
 *   window.DG_SYNC_CONFIG = {
 *     isLoggedIn: true, appProxyPath: "/apps/favoris"
 *   }
 */
(function () {
    'use strict';

    var CONFIG = window.DG_SYNC_CONFIG || {};
    var CUSTOMER_ID = CONFIG.customerId || null;
    var STORAGE_KEY = CUSTOMER_ID ? ('dg:fav:' + CUSTOMER_ID) : 'dg:fav';
    var APP_PROXY = (CONFIG.appProxyPath || '/apps/favoris').replace(/\/+$/, '');
    var IS_LOGGED_IN = !!(CONFIG.isLoggedIn && APP_PROXY);

    var ALLOWED_COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];
    var DEFAULT_COLOR = 'blue';

    var MAX_HANDLES = 500;
    var MAX_FOLDERS = 50;
    var MAX_FOLDER_NAME = 40;

    var _listeners = [];
    var _syncStatusListeners = [];
    var _syncing = false;
    var _syncPromise = null;
    var _postQueue = Promise.resolve();

    /**
     * Historique d'achats — la liste auto « Déjà commandé ».
     *
     * ⚠️ `_histMeta` est TRANSITOIRE et ne doit JAMAIS entrer dans l'état persisté.
     * Le POST de la vitrine envoie l'état COMPLET des favoris ; si les clés de
     * l'historique se retrouvaient dans `state.handles` ou `state.meta`, la
     * prochaine synchro transformerait des centaines de produits « déjà commandés »
     * en vrais favoris, sur tous les appareils du client et — pour une entreprise —
     * pour tous ses contacts. D'où une variable séparée, lue seulement par
     * `keyInfo` pour retrouver un handle au moment du rendu.
     */
    var _histMeta = {};
    var _histPromise = null;
    var _lastWriteAt = 0;
    var _lastServerPullAt = 0;
    var _pendingPush = false;
    var _syncGeneration = 0;
    var _syncStatus = IS_LOGGED_IN ? 'pending' : 'guest';
    var _serverEverLoaded = false;
    /** Item keys as of the last server acknowledgement — the baseline a 409 replays against. */
    var _dernierEtatConnu = [];
    var _conflitRejoue = false;

    // ── State helpers ─────────────────────────────────────────────────────────

    function emptyState() {
        return { handles: [], meta: {}, folders: [], revision: 0 };
    }

    function isValidColor(c) {
        return typeof c === 'string' && ALLOWED_COLORS.indexOf(c) !== -1;
    }

    /** h=handle, s=sku, t=title, u=1 unité / 0 caisse, av=1 disponible. */
    function normalizeMeta(info) {
        return {
            h: typeof info.h === 'string' ? info.h : '',
            s: typeof info.s === 'string' ? info.s : '',
            t: typeof info.t === 'string' ? info.t : '',
            u: typeof info.u === 'number' ? info.u : null,
            av: info.av === 0 ? 0 : 1
        };
    }

    /**
     * Coerce any input into a clean state object {handles, meta, folders, revision}.
     *
     * `handles` no longer carries product handles — it carries ITEM KEYS
     * ('v:<variantId>', or 'h:<handle>' for a legacy favorite we could not resolve
     * to a variant). The field name is kept because every consumer treats the
     * value as an opaque string; only `keyInfo()` ever looks inside one.
     *
     * Three accepted inputs, so a new client reads old data and an old server
     * payload without any migration step:
     *   · a bare array           → legacy, values are product handles
     *   · { handles, folders }   → legacy, values are product handles
     *   · { items, meta, folders[].items, revision } → current
     */
    function normalize(input) {
        if (!input) return emptyState();

        // Legacy: plain array of handles.
        if (Array.isArray(input)) {
            var st = emptyState();
            st.handles = cleanHandleList(input);
            return st;
        }

        if (typeof input !== 'object') return emptyState();

        var handles = cleanHandleList(Array.isArray(input.items) ? input.items : input.handles);
        var masterSet = {};
        for (var i = 0; i < handles.length; i++) masterSet[handles[i]] = true;

        // `meta` is display-only. Keeping entries for keys that are no longer
        // favorited would let a stale title survive a re-add.
        var meta = {};
        if (input.meta && typeof input.meta === 'object') {
            for (var mk in input.meta) {
                if (!Object.prototype.hasOwnProperty.call(input.meta, mk)) continue;
                if (!masterSet[mk]) continue;
                var mv = input.meta[mk];
                if (!mv || typeof mv !== 'object') continue;
                meta[mk] = {
                    h: typeof mv.h === 'string' ? mv.h : '',
                    s: typeof mv.s === 'string' ? mv.s : '',
                    t: typeof mv.t === 'string' ? mv.t : '',
                    u: typeof mv.u === 'number' ? mv.u : null,
                    av: mv.av === 0 ? 0 : 1
                };
            }
        }

        var folders = [];
        var seenIds = {};
        var rawFolders = Array.isArray(input.folders) ? input.folders : [];
        for (var j = 0; j < rawFolders.length && folders.length < MAX_FOLDERS; j++) {
            var f = rawFolders[j];
            if (!f || typeof f !== 'object') continue;
            var id = typeof f.id === 'string' ? f.id.trim() : '';
            var name = typeof f.name === 'string' ? f.name.trim() : '';
            var color = isValidColor(f.color) ? f.color : DEFAULT_COLOR;
            if (!id || seenIds[id]) continue;
            if (!name) continue;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);

            var fhRaw = Array.isArray(f.items) ? f.items : (Array.isArray(f.handles) ? f.handles : []);
            var fhSeen = {};
            var fh = [];
            for (var k = 0; k < fhRaw.length; k++) {
                var h = fhRaw[k];
                if (typeof h !== 'string' || !h) continue;
                if (!masterSet[h]) continue; // must be subset of master
                if (fhSeen[h]) continue;
                fhSeen[h] = true;
                fh.push(h);
            }

            seenIds[id] = true;
            folders.push({ id: id, name: name, color: color, handles: fh });
        }

        return {
            handles: handles,
            meta: meta,
            folders: folders,
            revision: typeof input.revision === 'number' ? input.revision : 0
        };
    }

    /**
     * Decompose an item key for rendering.
     * A guest, or a favorite created before the variant-level move, carries a bare
     * product handle as its key — that must keep rendering, just without a format.
     */
    function keyInfo(key, meta) {
        var m = (meta && meta[key]) || _histMeta[key];
        if (m && m.h) return { handle: m.h, variantId: key.indexOf('v:') === 0 ? key.slice(2) : null, meta: m };
        if (key.indexOf('v:') === 0) return { handle: null, variantId: key.slice(2), meta: m || null };
        if (key.indexOf('h:') === 0) return { handle: key.slice(2), variantId: null, meta: m || null };
        return { handle: key, variantId: null, meta: m || null };
    }

    function cleanHandleList(arr) {
        if (!Array.isArray(arr)) return [];
        var out = [];
        var seen = {};
        for (var i = 0; i < arr.length && out.length < MAX_HANDLES; i++) {
            var h = arr[i];
            if (typeof h !== 'string' || !h) continue;
            if (h.length > 200) continue;
            if (seen[h]) continue;
            seen[h] = true;
            out.push(h);
        }
        return out;
    }

    function genFolderId() {
        var rnd = Math.random().toString(36).slice(2, 8);
        var stamp = Date.now().toString(36).slice(-4);
        return 'f_' + stamp + rnd;
    }

    // ── localStorage helpers ──────────────────────────────────────────────────

    function readLocal() {
        try {
            var raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return emptyState();
            var parsed = JSON.parse(raw);
            return normalize(parsed);
        } catch (e) { return emptyState(); }
    }

    function writeLocal(state) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) { /* quota exceeded or unavailable */ }
    }

    // ── Migration: copy old keys into new key (one-time) ──────────────────────

    function migrateOldKeys() {
        try {
            var current = readLocal();
            if (current.handles.length > 0 || current.folders.length > 0) return;

            if (CUSTOMER_ID) {
                var generic = localStorage.getItem('dg:fav');
                if (generic) {
                    try {
                        var g = normalize(JSON.parse(generic));
                        if (g.handles.length > 0 || g.folders.length > 0) {
                            writeLocal(g);
                            console.debug('[dgFav] Migrated', g.handles.length, 'favorites from dg:fav →', STORAGE_KEY);
                            return;
                        }
                    } catch (e) { /* ignore */ }
                }
                var custLegacy = 'dg:favorites:' + CUSTOMER_ID;
                var rawCust = localStorage.getItem(custLegacy);
                if (rawCust) {
                    try {
                        var c = normalize(JSON.parse(rawCust));
                        if (c.handles.length > 0) {
                            writeLocal(c);
                            console.debug('[dgFav] Migrated from', custLegacy);
                            return;
                        }
                    } catch (e) { /* ignore */ }
                }
            }

            var legacyKeys = ['dg-favorites', 'dgFavorites', 'dg_favorites'];
            for (var i = 0; i < legacyKeys.length; i++) {
                var raw = localStorage.getItem(legacyKeys[i]);
                if (!raw) continue;
                try {
                    var parsed = JSON.parse(raw);
                    var migrated = normalize(parsed);
                    if (migrated.handles.length > 0) {
                        writeLocal(migrated);
                        console.debug('[dgFav] Migrated', migrated.handles.length, 'favorites from', legacyKeys[i]);
                        break;
                    }
                } catch (e) { /* ignore parse error */ }
            }
        } catch (e) { /* ignore migration errors */ }
    }

    function setSyncStatus(status) {
        _syncStatus = status;
        for (var i = 0; i < _syncStatusListeners.length; i++) {
            try { _syncStatusListeners[i](status); } catch (e) { /* ignore */ }
        }
    }

    function statesEqual(a, b) {
        a = normalize(a);
        b = normalize(b);
        if (a.handles.length !== b.handles.length || a.folders.length !== b.folders.length) return false;
        for (var i = 0; i < a.handles.length; i++) {
            if (b.handles.indexOf(a.handles[i]) === -1) return false;
        }
        return JSON.stringify(a.folders) === JSON.stringify(b.folders);
    }

    /** True when local was edited after the last successful server pull or push. */
    function hasUnsyncedLocalWrites() {
        return _pendingPush || _lastWriteAt > _lastServerPullAt;
    }

    /** Server still lists handles the user removed locally since last ack. */
    function localHasPendingDeletions(localState, serverState) {
        if (_lastWriteAt <= _lastServerPullAt) return false;
        var local = normalize(localState);
        var server = normalize(serverState);
        for (var i = 0; i < server.handles.length; i++) {
            if (local.handles.indexOf(server.handles[i]) === -1) return true;
        }
        return false;
    }

    function markServerAcknowledged() {
        _lastServerPullAt = Date.now();
        _dernierEtatConnu = readLocal().handles.slice();
        _conflitRejoue = false;
    }

    function pushPayloadFromLocal() {
        var s = readLocal();
        var folders = [];
        for (var i = 0; i < s.folders.length; i++) {
            var f = s.folders[i];
            folders.push({ id: f.id, name: f.name, color: f.color, items: f.handles });
        }
        // `revision` is what makes two writers safe: the server refuses a write
        // built on a revision it has already moved past, instead of silently
        // overwriting whoever wrote in between.
        return { revision: s.revision, items: s.handles, meta: s.meta, folders: folders };
    }

    function verifyServerMatchesLocal(attempt) {
        return workerFetch('GET').then(function (data) {
            if (!data || data.__stale) return false;
            var server = normalize(data);
            var local = readLocal();
            // Adopt the server's revision, otherwise the next write presents a
            // stale one and 409s forever.
            if (server.revision !== local.revision) {
                local.revision = server.revision;
                writeLocal(local);
            }
            if (statesEqual(server, local)) {
                markServerAcknowledged();
                console.debug('[dgFav] Server confirmed:', local.handles.length, 'fav');
                return true;
            }
            if (attempt < 3) {
                console.warn('[dgFav] Server mismatch after POST — retry', attempt + 1);
                return workerFetch('POST', pushPayloadFromLocal()).then(function () {
                    return verifyServerMatchesLocal(attempt + 1);
                });
            }
            console.error('[dgFav] Server did not persist deletions — update favoris-dg worker (full replace on POST)');
            return false;
        });
    }

    // ── Notify listeners ──────────────────────────────────────────────────────

    /**
     * The header badge (`[data-favorites-count]`, header-dg.liquid) has been in the
     * markup all along with nothing writing to it — permanently hidden at 0.
     */
    function refreshCountBadges(n) {
        try {
            var els = document.querySelectorAll('[data-favorites-count]');
            for (var i = 0; i < els.length; i++) {
                els[i].textContent = String(n);
                els[i].hidden = n === 0;
            }
        } catch (e) { /* pre-DOM or unsupported */ }
    }

    function notifyChange() {
        var state = readLocal();
        refreshCountBadges(state.handles.length);
        for (var i = 0; i < _listeners.length; i++) {
            try { _listeners[i](state.handles, state.folders); } catch (e) { /* listener error */ }
        }
    }

    // ── Worker communication ──────────────────────────────────────────────────

    function workerFetch(method, body) {
        if (!IS_LOGGED_IN) return Promise.resolve(null);

        var opts = {
            method: method,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        var url = APP_PROXY;
        if (method === 'GET') {
            url += (url.indexOf('?') >= 0 ? '&' : '?') + '_t=' + Date.now();
        } else {
            opts.body = JSON.stringify(body);
        }

        return fetch(url, opts)
            .then(function (res) {
                // 409 is not a failure: someone else (another device, or the team
                // in the Command Center) wrote first. The body carries the current
                // server state so we can replay our own edits on top of it.
                if (res.status === 409) {
                    return res.json().then(function (data) {
                        return { __stale: true, state: data };
                    }).catch(function () { return { __stale: true, state: null }; });
                }
                if (!res.ok) {
                    console.warn('[dgFav] Worker HTTP', res.status);
                    return null;
                }
                return res.json();
            })
            .catch(function (err) {
                console.warn('[dgFav] Worker error:', err.message);
                return null;
            });
    }

    /**
     * Replay local edits on top of the server state after a 409, ONCE.
     *
     * Union of both sides, minus what this session deliberately deleted since its
     * last acknowledged pull. Looping here would be worse than losing the edit:
     * two clients could ping-pong 409s forever, so a second failure gives up and
     * surfaces the conflict.
     */
    function resoudreConflit(serverState) {
        var local = readLocal();
        var server = normalize(serverState);

        var supprimesLocalement = {};
        if (_lastWriteAt > _lastServerPullAt) {
            for (var i = 0; i < _dernierEtatConnu.length; i++) {
                var ancien = _dernierEtatConnu[i];
                if (local.handles.indexOf(ancien) === -1) supprimesLocalement[ancien] = true;
            }
        }

        var fusion = server.handles.slice();
        for (var j = 0; j < local.handles.length; j++) {
            if (fusion.indexOf(local.handles[j]) === -1) fusion.push(local.handles[j]);
        }
        fusion = fusion.filter(function (k) { return !supprimesLocalement[k]; });

        var meta = {};
        for (var mk in server.meta) if (Object.prototype.hasOwnProperty.call(server.meta, mk)) meta[mk] = server.meta[mk];
        for (var lk in local.meta) if (Object.prototype.hasOwnProperty.call(local.meta, lk)) meta[lk] = local.meta[lk];

        // Folders: server wins on naming, local memberships are merged in.
        var folders = server.folders.map(function (sf) {
            var lf = null;
            for (var n = 0; n < local.folders.length; n++) {
                if (local.folders[n].id === sf.id) { lf = local.folders[n]; break; }
            }
            var h = sf.handles.slice();
            if (lf) {
                for (var q = 0; q < lf.handles.length; q++) {
                    if (h.indexOf(lf.handles[q]) === -1) h.push(lf.handles[q]);
                }
            }
            return { id: sf.id, name: sf.name, color: sf.color, handles: h };
        });
        for (var r = 0; r < local.folders.length; r++) {
            var exists = false;
            for (var s2 = 0; s2 < folders.length; s2++) {
                if (folders[s2].id === local.folders[r].id) { exists = true; break; }
            }
            if (!exists) folders.push(local.folders[r]);
        }

        var fusionne = normalize({
            items: fusion, meta: meta, revision: server.revision,
            folders: folders.map(function (f) {
                return { id: f.id, name: f.name, color: f.color, items: f.handles };
            })
        });
        writeLocal(fusionne);
        _dernierEtatConnu = fusionne.handles.slice();
        notifyChange();
        return fusionne;
    }

    function enqueueServerWrite() {
        _pendingPush = true;
        _postQueue = _postQueue
            .catch(function () {
                // Keep the queue alive after a failed write.
            })
            .then(function () {
                var s = readLocal();
                return workerFetch('POST', pushPayloadFromLocal()).then(function (ok) {
                    if (ok === null) return;

                    if (ok.__stale) {
                        if (_conflitRejoue) {
                            // Second conflict in a row: stop. Retrying forever would
                            // let two devices ping-pong 409s and never settle.
                            console.warn('[dgFav] Conflit persistant — arrêt du rejeu.');
                            setSyncStatus('conflict');
                            if (ok.state) resoudreConflit(ok.state);
                            return;
                        }
                        _conflitRejoue = true;
                        console.debug('[dgFav] 409 — rejeu du delta local sur l’état serveur.');
                        resoudreConflit(ok.state);
                        return workerFetch('POST', pushPayloadFromLocal()).then(function (ok2) {
                            if (ok2 && !ok2.__stale) {
                                writeLocal(normalize(ok2));
                                markServerAcknowledged();
                                notifyChange();
                                setSyncStatus('ok');
                            } else {
                                setSyncStatus('conflict');
                            }
                        });
                    }

                    console.debug('[dgFav] POST sent:', s.handles.length, 'fav,', s.folders.length, 'folders');
                    // The response IS the authoritative new state (revision included).
                    var confirme = normalize(ok);
                    var local = readLocal();
                    if (confirme.revision > local.revision) {
                        local.revision = confirme.revision;
                        writeLocal(local);
                    }
                    return verifyServerMatchesLocal(0);
                });
            })
            .finally(function () {
                _pendingPush = false;
            });
        return _postQueue;
    }

    /**
     * Push the latest local state to the server. Waits for any in-flight
     * GET sync to settle first, then pushes the freshest local snapshot.
     */
    function pushLocalToServer() {
        if (!IS_LOGGED_IN) return Promise.resolve();
        var doSync = enqueueServerWrite;
        if (_syncPromise) {
            return _syncPromise.then(doSync);
        }
        return doSync();
    }

    /**
     * Mutate local state via mutator(state) -> mutated state, then write,
     * mark _lastWriteAt, notify listeners, push to server.
     */
    function mutate(mutator) {
        var s = readLocal();
        var next = mutator(s);
        if (!next) return false;
        writeLocal(next);
        _lastWriteAt = Date.now();
        notifyChange();
        pushLocalToServer();
        return true;
    }

    // ══════════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ══════════════════════════════════════════════════════════════════════════

    window.dgFav = {

        list: function () {
            return readLocal().handles;
        },

        has: function (handle) {
            if (!handle) return false;
            return readLocal().handles.indexOf(handle) !== -1;
        },

        /**
         * @param key   item key ('v:<variantId>') — or a bare product handle for a
         *              guest / legacy caller.
         * @param info  optional {h, s, t, u} snapshot used to render the card.
         *
         * Goes through `mutate()` rather than re-implementing the write: the old
         * inline version bypassed it, so the hottest path in the whole feature
         * (clicking a heart) was the one path that skipped revision handling.
         */
        toggle: function (key, info) {
            if (!key) return false;
            var nowWishlisted = null;
            mutate(function (state) {
                var idx = state.handles.indexOf(key);
                if (idx === -1) {
                    if (state.handles.length >= MAX_HANDLES) {
                        console.warn('[dgFav] Max favorites reached:', MAX_HANDLES);
                        nowWishlisted = null;
                        return null;
                    }
                    state.handles.push(key);
                    if (info) state.meta[key] = normalizeMeta(info);
                    nowWishlisted = true;
                } else {
                    state.handles.splice(idx, 1);
                    delete state.meta[key];
                    // Cascade: remove from every folder.
                    for (var i = 0; i < state.folders.length; i++) {
                        var fh = state.folders[i].handles;
                        var fIdx = fh.indexOf(key);
                        if (fIdx !== -1) fh.splice(fIdx, 1);
                    }
                    nowWishlisted = false;
                }
                return state;
            });
            return nowWishlisted === null ? false : nowWishlisted;
        },

        /** Add without toggling off — used when the caller knows it wants it favorited. */
        add: function (key, info) {
            if (!key) return false;
            var added = false;
            mutate(function (state) {
                if (state.handles.indexOf(key) !== -1) {
                    if (info) state.meta[key] = normalizeMeta(info);
                    added = true;
                    return state;
                }
                if (state.handles.length >= MAX_HANDLES) return null;
                state.handles.push(key);
                if (info) state.meta[key] = normalizeMeta(info);
                added = true;
                return state;
            });
            return added;
        },

        /** Display snapshot for an item key: {h, s, t, u, av} or null. */
        meta: function (key) {
            var m = readLocal().meta[key];
            return m ? { h: m.h, s: m.s, t: m.t, u: m.u, av: m.av } : null;
        },

        /** Product handle + variant id behind an item key (handles legacy keys). */
        keyInfo: function (key) {
            return keyInfo(key, readLocal().meta);
        },

        /**
         * Historique d'achats des 6 derniers mois — liste AUTO, en lecture seule.
         *
         * Rend `{ items, depuis, fenetreMois, couvertureShopifyDepuis, tronque }`,
         * items triés du plus récemment commandé au plus ancien. Mémoïsé pour la
         * durée de la page : c'est une lecture agrégée, pas un état à surveiller.
         *
         * Les métadonnées vont dans `_histMeta` (transitoire) et JAMAIS dans
         * l'état persisté — voir le commentaire de `_histMeta`. Cette fonction ne
         * doit donc jamais appeler `mutate()` ni `syncToServer()`.
         */
        historique: function () {
            if (_histPromise) return _histPromise;
            if (!IS_LOGGED_IN) return Promise.resolve(null);

            _histPromise = fetch(APP_PROXY + '/historique?_t=' + Date.now(), {
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' }
            })
                .then(function (res) {
                    if (!res.ok) {
                        console.warn('[dgFav] historique HTTP', res.status);
                        return null;
                    }
                    return res.json();
                })
                .then(function (data) {
                    if (!data || !Array.isArray(data.items)) return null;
                    for (var i = 0; i < data.items.length; i++) {
                        var it = data.items[i];
                        if (!it || !it.itemKey) continue;
                        _histMeta[it.itemKey] = {
                            h: it.handle || null,
                            s: it.sku || null,
                            t: it.title || null,
                            u: typeof it.isUnit === 'number' ? it.isUnit : null,
                            av: it.indisponible ? 0 : 1
                        };
                    }
                    return data;
                })
                .catch(function (err) {
                    console.warn('[dgFav] historique', err);
                    // Ne pas mémoïser un échec : un réseau qui retombe doit pouvoir
                    // resservir la liste sans recharger la page.
                    _histPromise = null;
                    return null;
                });

            return _histPromise;
        },

        remove: function (handle) {
            if (!handle) return;
            mutate(function (state) {
                var idx = state.handles.indexOf(handle);
                if (idx === -1) return null;
                state.handles.splice(idx, 1);
                delete state.meta[handle];
                for (var i = 0; i < state.folders.length; i++) {
                    var fh = state.folders[i].handles;
                    var fIdx = fh.indexOf(handle);
                    if (fIdx !== -1) fh.splice(fIdx, 1);
                }
                return state;
            });
        },

        /** Retire des favoris les handles dont le produit n'existe plus (un seul sync). */
        pruneHandles: function (handles) {
            if (!Array.isArray(handles) || !handles.length) return 0;
            var removed = 0;
            var ok = mutate(function (state) {
                for (var i = 0; i < handles.length; i++) {
                    var h = handles[i];
                    if (typeof h !== 'string' || !h) continue;
                    var idx = state.handles.indexOf(h);
                    if (idx === -1) continue;
                    state.handles.splice(idx, 1);
                    delete state.meta[h];
                    removed++;
                    for (var f = 0; f < state.folders.length; f++) {
                        var fh = state.folders[f].handles;
                        var fIdx = fh.indexOf(h);
                        if (fIdx !== -1) fh.splice(fIdx, 1);
                    }
                }
                return removed > 0 ? state : null;
            });
            return ok ? removed : 0;
        },

        onChange: function (fn) {
            if (typeof fn === 'function') _listeners.push(fn);
        },

        // ── Folders ───────────────────────────────────────────────────────────

        folders: function () {
            return readLocal().folders.map(function (f) {
                return { id: f.id, name: f.name, color: f.color, handles: f.handles.slice() };
            });
        },

        foldersOf: function (handle) {
            if (!handle) return [];
            var state = readLocal();
            var ids = [];
            for (var i = 0; i < state.folders.length; i++) {
                if (state.folders[i].handles.indexOf(handle) !== -1) {
                    ids.push(state.folders[i].id);
                }
            }
            return ids;
        },

        colors: function () {
            return ALLOWED_COLORS.slice();
        },

        createFolder: function (name, color) {
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return null;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            color = isValidColor(color) ? color : DEFAULT_COLOR;

            var state = readLocal();
            if (state.folders.length >= MAX_FOLDERS) {
                console.warn('[dgFav] Max folders reached:', MAX_FOLDERS);
                return null;
            }
            var id = genFolderId();
            var folder = { id: id, name: name, color: color, handles: [] };
            state.folders.push(folder);
            writeLocal(state);
            _lastWriteAt = Date.now();
            notifyChange();
            pushLocalToServer();
            return { id: folder.id, name: folder.name, color: folder.color, handles: [] };
        },

        renameFolder: function (id, name) {
            if (!id) return false;
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return false;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].name = name;
                        return state;
                    }
                }
                return null;
            });
        },

        setFolderColor: function (id, color) {
            if (!id || !isValidColor(color)) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].color = color;
                        return state;
                    }
                }
                return null;
            });
        },

        deleteFolder: function (id) {
            if (!id) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders.splice(i, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        addToFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                if (state.handles.indexOf(handle) === -1) return null; // not a favorite
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        if (state.folders[i].handles.indexOf(handle) !== -1) return null; // already in
                        state.folders[i].handles.push(handle);
                        return state;
                    }
                }
                return null;
            });
        },

        removeFromFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        var idx = state.folders[i].handles.indexOf(handle);
                        if (idx === -1) return null;
                        state.folders[i].handles.splice(idx, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        // ── Sync ──────────────────────────────────────────────────────────────

        syncFromServer: function () {
            if (!IS_LOGGED_IN) {
                setSyncStatus('guest');
                return Promise.resolve();
            }
            if (_syncPromise) return _syncPromise;

            _syncing = true;
            setSyncStatus('pending');
            var syncGeneration = ++_syncGeneration;

            function fetchWithRetry(attempt) {
                return workerFetch('GET').then(function (data) {
                    if (data) return data;
                    if (attempt >= 2) return null;
                    return new Promise(function (resolve) {
                        setTimeout(function () {
                            resolve(fetchWithRetry(attempt + 1));
                        }, 400 * attempt);
                    });
                });
            }

            _syncPromise = fetchWithRetry(1)
                .then(function (data) {
                    if (syncGeneration !== _syncGeneration) return;

                    if (hasUnsyncedLocalWrites()) {
                        console.debug('[dgFav] Local changes pending — pushing to server');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    var local = readLocal();

                    if (!data) {
                        console.warn('[dgFav] Sync failed — keeping local, will retry');
                        setSyncStatus('error');
                        return;
                    }

                    // Pass `data` whole: it carries `items`, `meta` and `revision`
                    // that the old destructuring silently dropped.
                    var server = normalize(data);
                    _serverEverLoaded = true;

                    if (localHasPendingDeletions(local, server)) {
                        console.debug('[dgFav] Pending deletions on server — forcing push');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    // First-time bootstrap: server empty, local has favorites → upload local.
                    var serverEmpty = server.handles.length === 0 && server.folders.length === 0;
                    var localHasData = local.handles.length > 0 || local.folders.length > 0;
                    if (serverEmpty && localHasData) {
                        console.debug('[dgFav] Bootstrapping server from local');
                        setSyncStatus('ok');
                        return enqueueServerWrite();
                    }

                    // Pull: server is source of truth (fixes cross-device ghost re-adds).
                    if (!statesEqual(server, local)) {
                        writeLocal(server);
                        markServerAcknowledged();
                        console.debug('[dgFav] Synced from server:',
                            server.handles.length, 'fav,',
                            server.folders.length, 'folders');
                        notifyChange();
                    } else {
                        markServerAcknowledged();
                        console.debug('[dgFav] Synced (already up to date)');
                    }

                    setSyncStatus('ok');
                })
                .catch(function (err) {
                    console.warn('[dgFav] Sync error:', err.message || err);
                    setSyncStatus('error');
                })
                .then(function () {
                    _syncing = false;
                    _syncPromise = null;
                });

            return _syncPromise;
        },

        getSyncStatus: function () {
            return _syncStatus;
        },

        onSyncStatus: function (fn) {
            if (typeof fn === 'function') _syncStatusListeners.push(fn);
        },

        syncToServer: function () {
            if (!IS_LOGGED_IN) return Promise.resolve();
            var s = readLocal();
            return enqueueServerWrite().then(function () {
                console.debug('[dgFav] Pushed', s.handles.length, 'fav /', s.folders.length, 'folders');
            });
        },

        isSyncEnabled: function () {
            return IS_LOGGED_IN;
        }
    };

    // ── Auto-init ─────────────────────────────────────────────────────────────

    migrateOldKeys();

    // Paint the badge from localStorage before any network round-trip, otherwise
    // it reads 0 on every page load until the sync lands.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            refreshCountBadges(readLocal().handles.length);
        });
    } else {
        refreshCountBadges(readLocal().handles.length);
    }

    if (IS_LOGGED_IN) {
        window.dgFav.syncFromServer();
        console.debug('[dgFav] Init: logged in — sync via', APP_PROXY, 'storage', STORAGE_KEY);

        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') {
                window.dgFav.syncFromServer();
            }
        });
        window.addEventListener('pageshow', function (e) {
            if (e.persisted) window.dgFav.syncFromServer();
        });
    } else {
        setSyncStatus('guest');
        console.debug('[dgFav] Init: guest mode (localStorage only)');
    }

})();
